import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

// ── "ACTIVE USERS" ───────────────────────────────────────────────────────────
// One marker per user per UTC day, at activity/{YYYY-MM-DD}/users/{uid}. Its
// existence is the whole datum, which is what makes the count a count of
// PEOPLE rather than of visits.
//
// ONE WRITE PER USER PER DAY, AND NOT ONE MORE. A localStorage stamp means
// navigating around the app, reloading, or opening ten tabs all cost nothing
// after the first. Without it this would be a write on every mount — the exact
// per-action billing the whole aggregation design exists to avoid.
//
// The stamp is a cache, not the authority: clearing it costs one redundant
// write, and the rules make the marker create-only so a second write is
// refused anyway. Losing a marker is likewise harmless — it undercounts one
// person by one day, and nothing else depends on it.

const KEY = 'tunify.active.v1';

const dayKey = () => new Date().toISOString().slice(0, 10);

/**
 * Records that this user was here today. Safe to call on every mount.
 *
 * Never throws and never reports: an analytics marker must not be able to
 * break a page, and a signed-out or offline user simply does not count.
 */
export async function markActiveToday(uid) {
  if (!uid) return;
  const today = dayKey();
  const stamp = `${uid}:${today}`;

  try {
    if (localStorage.getItem(KEY) === stamp) return;   // already counted today
  } catch { /* private mode: fall through and just write */ }

  try {
    await setDoc(
      doc(db, 'activity', today, 'users', uid),
      { at: serverTimestamp() },
      // A create in practice — the rules refuse updates. `merge` keeps a
      // second attempt from erroring loudly if the stamp was lost.
      { merge: true },
    );
    try { localStorage.setItem(KEY, stamp); } catch { /* private mode */ }
  } catch {
    // Offline, rules, anything — the app carries on unaffected. The stamp is
    // deliberately NOT set, so the next mount tries again.
  }
}

export default markActiveToday;
